import React, { useState, useEffect, useRef, useCallback } from 'react'
import classNames from 'classnames'
import Ratio from 'react-bootstrap/Ratio'
import { Spinner } from 'react-bootstrap'

import EventsCard from '../EventsCard/EventsCard'
import ErrorComponent from '../ErrorComponent/ErrorComponent'
import DataNotfound from '../DataNotfound/DataNotfound'
import EventsFilter from './EventsFilter'
import Paginator from '../Paginator/Paginator'
import useFetchFilteredData from '../../hooks/useFetchFilteredData'
import useIsMobile from '../../hooks/useIsMobile'
import { getTotalPages } from '../../utils'
import { mediaParamList } from '../../Contansts'

import styles from './EventsLayout.module.scss'

const EventsLayout = ({
  data,
  pagination: initialPagination,
  emptyMessage,
}) => {
  const { event_filter, sort, filter_title } = data || {}

  const [currentPage, setCurrentPage] = useState(1)
  const [selectedFilter, setSelectedFilter] = useState({
    selectedEvent: event_filter?.list[0],
    selectedSort: sort?.list[0],
  })
  const [totalPages, setTotalPages] = useState(getTotalPages(initialPagination))
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const loaderRef = useRef(null)
  const [cards, setCards] = useState(data?.folder || [])
  const isMobile = useIsMobile()

  const {
    data: filteredData,
    loading,
    error,
    setParams,
  } = useFetchFilteredData(mediaParamList['events-and-awards'])

  useEffect(() => {
    if (isInitialLoading) {
      return
    }
    const { result, pagination } = filteredData?.data || {}
    if (pagination) {
      setTotalPages(getTotalPages(pagination))
    }
    if (isMobile) {
      const folder = result?.sectiondata?.folder || []
      setCards((prevData) => [...prevData, ...folder])
    } else {
      setCards(result?.sectiondata?.folder || [])
    }
  }, [filteredData])

  const handleFilterChange = (e, option, key) => {
    setSelectedFilter((prevState) => ({
      ...prevState,
      [key]: {
        ...option,
      },
    }))
    setCurrentPage(1)
    setCards([])
  }

  useEffect(() => {
    if (isInitialLoading) {
      setIsInitialLoading(false)
      return
    }
    const params = `events_type=${selectedFilter?.selectedEvent?.value || ''}&sortOrder=${selectedFilter?.selectedSort?.value || ''}&page=${currentPage}`
    setParams(params)
  }, [selectedFilter, currentPage])

  const lazyLoad = useCallback(
    (entries) => {
      const target = entries[0]
      if (target.isIntersecting && !loading) {
        if (currentPage < totalPages) {
          setCurrentPage((prevPage) => prevPage + 1)
        }
      }
    },
    [loading, totalPages, filteredData]
  )

  useEffect(() => {
    if (isMobile) {
      const observer = new IntersectionObserver(lazyLoad)
      if (loaderRef.current) {
        observer.observe(loaderRef.current)
      }
      return () => {
        if (loaderRef.current) {
          observer.unobserve(loaderRef.current)
        }
      }
    }
  }, [lazyLoad])

  const renderEvents = () => {
    if (loading && !isMobile) {
      return (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )
    }

    if (error?.hasError) {
      return (
        <div className="flex justify-center">
          <ErrorComponent
            errorMessage={error.errorMessage}
            renderProps={() => <div>{error.errorMessage}</div>}
          />
        </div>
      )
    }

    if (cards && cards.length > 0) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards?.map((album) => {
            return (
              <Ratio aspectRatio="1x1" key={'album' + album?.id}>
                <EventsCard data={album} />
              </Ratio>
            )
          })}
          {loading && isMobile && (
            <div className="flex justify-center">
              <Spinner />
            </div>
          )}
          <div ref={loaderRef}></div>
        </div>
      )
    }
    if (cards?.length === 0) {
      return <DataNotfound description={emptyMessage} />
    }
  }

  return (
    <>
      <div className={classNames(styles['filter-root-container'], 'mb-10')}>
        {filter_title && (
          <div className={styles['filter-title']}>{filter_title}</div>
        )}
        <EventsFilter
          eventFilter={event_filter}
          sortFilter={sort}
          handleFilterChange={handleFilterChange}
          selectedFilter={selectedFilter}
        />
      </div>
      {renderEvents()}
      {totalPages > 0 && cards.length > 0 && (
        <div className="items-center justify-center hidden md:flex">
          <Paginator
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            numOfPage={totalPages}
          />
        </div>
      )}
    </>
  )
}

export default EventsLayout
